/**
 * Scroll-triggered reveal animation using IntersectionObserver.
 * Calls `onReveal` callback once the element enters the viewport.
 *
 * @param threshold — visibility threshold (default 0.15)
 * @param rootMargin — root margin for observer (default '0px')
 * @param once — fire only once (default true)
 *
 * @example
 * ```vue
 * <script setup>
 * const { isVisible } = useRevealAnimation({ threshold: 0.2 });
 * </script>
 * <template>
 *   <div ref="el" :class="{ revealed: isVisible }">content</div>
 * </template>
 * ```
 */
interface RevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

export function useRevealAnimation(options: RevealOptions = {}) {
  const { threshold = 0.15, rootMargin = '0px', once = true } = options;

  const el = ref<HTMLElement | null>(null);
  const isVisible = ref(false);

  onMounted(() => {
    if (!el.value) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          isVisible.value = true;
          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          isVisible.value = false;
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el.value);

    onBeforeUnmount(() => {
      observer.disconnect();
    });
  });

  return { el, isVisible };
}
